import React, { useEffect, useRef, useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import i18next from '../../../i18n';
import styles from './ProductListingPage.module.scss';
import AppPaths from '../../AppPaths';
import { getDomainConfig } from '../../lib/domainConfig';
import { series3PlusCollarId } from '../../types/Product';
import { inAppShop, partnerLandingPage, productListingPage, quizQuestionPage } from '../../lib/analytics/events';
import { getPartnerSlugForCode } from '../../views/PartnerLanding/partnersRegistry';
import { usePartnerLandingEnabled } from '../../views/PartnerLanding/usePartnerLandingEnabled';
import FiMiniImage from '../../assets/images/plp-fi-mini.png';
import FiSeries3PlusImage from '../../assets/images/plp-fi-series-three-plus.png';
import FiUltraImage from '../../assets/images/plp-fi-ultra.png';
import FiMiniForCatsImage from '../../assets/images/plp-fi-mini-cats.png';
import FiAccessoriesImage from '../../assets/images/plp-accessories.png';
import FiSupplementsImage from '../../assets/images/plp-supplements.png';
import FiNanoMicrochipsImage from '../../assets/images/plp-nano.png';
import { ReactComponent as DownArrow } from '../../assets/images/icons/arrow_down.svg';
import { ReactComponent as UpArrow } from '../../assets/images/icons/arrow_up.svg';
import { ReactComponent as Check } from '../../assets/images/check.svg';
import { ReactComponent as IconMagic } from '../../assets/images/icons/icon_magic.svg';
import { ReactComponent as Dog } from '../../assets/images/icons/dog.svg';
import { ReactComponent as Cat } from '../../assets/images/icons/cat.svg';
import classNames from 'classnames';
import {
  collarProductIsSupportedInCountry,
  ultraIsDiscoverable,
  nanoProductIsSupportedInCountry,
  supplementsProductIsSupportedInCountry,
  useLocale,
} from '../../contexts/LocaleContext';
import * as types from '../../types';
import { Button, Flex, Text } from '@mantine/core';
import { SaleBadge } from '../SaleBadge/SaleBadge';
import { useIsGrowthBookEnabled } from '../../lib/growthbook';
import { useLaunchQuiz } from '../../lib/fiQuiz';
import FreeTrialCard from '../FreeTrialCard/FreeTrialCard';
import { getUrlPartnerCode } from '../../lib/giftWithPurchasePromo';
import { isInAppShop } from '../../lib/inAppShop';
import useFreeTrialPromoVisible from '../../hooks/useFreeTrialPromoVisible';
import useIsReferralCheckoutV2 from '../../hooks/useIsReferralCheckoutV2';
import StarRating from '../StarRating/StarRating';

function useRedeemFlow() {
  const discountParam = getUrlPartnerCode();
  const isRedeemFlow = !!discountParam?.trim();

  return { discountParam, isRedeemFlow };
}

type PlpCardLabel = string;

function getPlpCardLabel(title: string, subtitle?: string): PlpCardLabel {
  const series3PlusTitle = i18next.t('series_3_plus');
  const miniTitle = i18next.t('mini');
  const ultraTitle = i18next.t('fi_ultra');
  const forCatsSubtitle = i18next.t('for_cats');

  if (title === series3PlusTitle) return i18next.t('series_3_plus');
  if (title === ultraTitle) return i18next.t('fi_ultra');
  if (title === miniTitle && subtitle && subtitle.toUpperCase().includes(forCatsSubtitle.toUpperCase()))
    return i18next.t('mini_cats');
  return i18next.t('mini');
}

function trackDetailsToggle(title: string, subtitle: string | undefined, expanded: boolean) {
  const label = getPlpCardLabel(title, subtitle);
  const miniLabel = i18next.t('mini');
  const series3PlusLabel = i18next.t('series_3_plus');
  const miniCatsLabel = i18next.t('mini_cats');
  const ultraLabel = i18next.t('fi_ultra');

  if (label === miniLabel) {
    expanded ? productListingPage.miniDetailsExpand() : productListingPage.miniDetailsCollapse();
  } else if (label === series3PlusLabel) {
    expanded ? productListingPage.series3PlusDetailsExpand() : productListingPage.series3PlusDetailsCollapse();
  } else if (label === miniCatsLabel) {
    expanded ? productListingPage.miniCatsDetailsExpand() : productListingPage.miniCatsDetailsCollapse();
  } else if (label === ultraLabel) {
    expanded ? productListingPage.ultraDetailsExpand() : productListingPage.ultraDetailsCollapse();
  }
}

type BetterForProps = { children: React.ReactNode; color: string; icon: string };
const ProductCardBetterFor: React.FC<BetterForProps> = ({ children, color, icon }) => (
  <span className={classNames(styles.betterForItem, styles[color])}>
    {icon === 'dog' ? <Dog /> : <Cat />}
    {children}
  </span>
);

type CardProps = {
  title: string;
  subtitle?: string;
  description: string;
  image: string;
  cta: string;
  onClick: () => void;
  hardware?: (string | { text: string; icon: React.ReactNode })[];
  betterFor?: { color: string; label: string; icon: string }[];
  showSaleBadge?: boolean;
  freeTrialAvailable?: boolean;
};

const ProductCard: React.FC<CardProps> = ({
  title,
  subtitle,
  description,
  image,
  cta,
  onClick,
  hardware = [],
  betterFor = [],
  showSaleBadge = false,
  freeTrialAvailable = false,
}) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const hasDetails = hardware.length > 0 || betterFor.length > 0;

  return (
    <div className={styles.productCard}>
      <img className={styles.cardImage} src={image} alt={title} loading="lazy" />
      <div className={styles.cardBody}>
        <div className={styles.cardTitle}>
          {title}
          <div className={styles.cardTitleSubtitle}>{subtitle}</div>
          {showSaleBadge && <SaleBadge className={styles.saleBadge} />}
        </div>
        <div className={styles.cardDescription}>{description}</div>
        <div className={styles.cardButtonRow}>
          <button className={styles.cardButton} onClick={onClick}>
            {cta}
          </button>
          {freeTrialAvailable && <div className={styles.freeTrialAvailableText}>{t('free_trial')}</div>}
        </div>
        {hasDetails && (
          <>
            <div className={styles.divider} />
            <button
              type="button"
              className={styles.detailsToggle}
              aria-expanded={showDetails}
              onClick={() =>
                setShowDetails((s) => {
                  const next = !s;
                  trackDetailsToggle(title, subtitle, next);
                  return next;
                })
              }
            >
              <div className={styles.closeContainer}>
                <span className={styles.closeContainerTextMobile}>{t('hide_details')}</span>
                <span className={styles.closeContainerTextDesktop}>{t('details')}</span>
                <UpArrow />
              </div>
              <div className={styles.openContainer}>
                <span className={styles.openContainerTextMobile}>{t('see_details')}</span>
                <span className={styles.openContainerTextDesktop}>{t('details')}</span>
                <DownArrow />
              </div>
            </button>

            <div className={classNames(styles.detailsSection, { [styles.open]: showDetails })}>
              <div className={styles.hardwareTitle}>{t('hardware_no_colon')}</div>
              <div className={styles.hardware}>
                {hardware.map((h) => {
                  const isCustom = typeof h !== 'string';
                  return (
                    <div className={styles.hardwareItem} key={isCustom ? h.text : h}>
                      {isCustom ? h.icon : <Check />}
                      {isCustom ? h.text : h}
                    </div>
                  );
                })}
              </div>
              <div className={styles.betterForTitle}>{t('better_for')}</div>
              <div className={styles.betterFor}>
                {betterFor.map((a) => (
                  <ProductCardBetterFor key={a.label} color={a.color} icon={a.icon}>
                    {a.label}
                  </ProductCardBetterFor>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ProductCarousel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={styles.carousel}>{children}</div>
);

type OrderableProductCard = { key: string; visible: boolean; node: React.ReactNode };

/**
 * Orders the main product cards by the `o` query param (e.g. `?o=u-s3-m-mc`).
 * Tokens are hyphen-separated card keys; unknown or duplicate tokens are
 * ignored, and any visible card the param omits is appended in its default
 * position so a partial `o` never silently hides a product. Hidden cards
 * (country/flow gated) are never surfaced even when explicitly requested.
 * Falls back to the default order when the param is absent.
 */
function orderProductCards(cards: OrderableProductCard[], orderParam: string | null): OrderableProductCard[] {
  const visible = cards.filter((c) => c.visible);
  if (!orderParam) return visible;

  const byKey = new Map(visible.map((c) => [c.key, c]));
  const seen = new Set<string>();
  const ordered = orderParam
    .split('-')
    .map((token) => token.trim().toLowerCase())
    .flatMap((key) => {
      const card = byKey.get(key);
      if (!card || seen.has(key)) return [];
      seen.add(key);
      return [card];
    });

  return [...ordered, ...visible.filter((c) => !seen.has(c.key))];
}

const ProductListingPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const launchQuiz = useLaunchQuiz();
  const { search } = useLocation();
  const scrollFiredRef = useRef<{ [k: string]: boolean }>({ '50': false, '75': false, '100': false });
  const { country } = useLocale();
  const internalTester = useSelector((state: types.AppState) => !!state.config.siteConfig.internalTester);
  const { discountParam, isRedeemFlow } = useRedeemFlow();
  // If the `ecom-partner-landing-pages` GrowthBook flag is on (prod) or we're
  // in a non-prod build, and the partner code's prefix maps to a registered
  // partner landing page, redirect there while preserving the full query
  // string (so `?pc=...` is kept).
  const partnerLandingEnabled = usePartnerLandingEnabled();
  const partnerSlugRedirect = partnerLandingEnabled ? getPartnerSlugForCode(discountParam) : null;
  const isUltraSupportedInCountry = useIsGrowthBookEnabled() && ultraIsDiscoverable(country, internalTester);
  const isFreeTrialVisible = useFreeTrialPromoVisible();
  const inApp = isInAppShop();
  const isReferralV2 = useIsReferralCheckoutV2();
  const referrerName = useSelector((state: types.AppState) => state.storeShop.cart.referrerName);

  useEffect(() => {
    if (partnerSlugRedirect) {
      partnerLandingPage.redirectedFromPlp(partnerSlugRedirect);
      // Skip PLP analytics + scroll tracking for redirected sessions so the
      // partner landing gets the page-view signal instead of the PLP.
      return;
    }
    productListingPage.pageViewed();
    if (inApp) {
      inAppShop.opened();
    }

    const onScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      const height = (document.documentElement.scrollHeight || 0) - window.innerHeight;
      if (height <= 0) {
        return;
      }
      const pct = (scrollTop / height) * 100;
      if (!scrollFiredRef.current['50'] && pct >= 50) {
        productListingPage.scroll50();
        scrollFiredRef.current['50'] = true;
      }
      if (!scrollFiredRef.current['75'] && pct >= 75) {
        productListingPage.scroll75();
        scrollFiredRef.current['75'] = true;
      }
      if (!scrollFiredRef.current['100'] && pct >= 100) {
        productListingPage.scroll100();
        scrollFiredRef.current['100'] = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true } as any);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll as any);
  }, [partnerSlugRedirect, inApp]);

  // Country gating runs BEFORE the partner redirect so that non-Mini countries
  // continue to land on the Series 3+ PDP as they did before this feature.
  // Partner pages surface a Mini CTA unconditionally, so redirecting Mini-
  // unsupported countries into them would leak a broken call-to-action.
  // (Today the only Mini-supported country is US — see LocaleContext.)
  if (!collarProductIsSupportedInCountry(types.miniCollarId, country)) {
    return <Redirect to={AppPaths.Products(types.series3PlusCollarId)} />;
  }

  if (partnerSlugRedirect) {
    return <Redirect to={`${AppPaths.Partners.LandingFor(partnerSlugRedirect)}${search}`} />;
  }

  const miniBatteryLife = t('up_to_2_months_battery_life');

  // Card display order can be overridden via `?o=` (e.g. `?o=u-s3-m-mc`).
  const orderParam = new URLSearchParams(search).get('o');
  const productCards: OrderableProductCard[] = [
    {
      key: 's3',
      visible: true,
      node: (
        <ProductCard
          title="Series 3+"
          description={t('product_description_series_3_plus')}
          image={FiSeries3PlusImage}
          cta={isRedeemFlow ? t('claim_free_series_3_plus') : t('shop_fi_series_3_plus')}
          onClick={() => {
            productListingPage.series3PlusShopNow();
            history.push(
              isRedeemFlow
                ? `${AppPaths.Redeem}?discount=${encodeURIComponent(discountParam ?? '')}`
                : AppPaths.Products(series3PlusCollarId),
            );
          }}
          hardware={[
            t('high_durability'),
            t('built_directly_into_the_collar'),
            t('up_to_3_months_of_battery_life'),
            { text: t('fi_intelligence_enabled'), icon: <IconMagic /> },
          ]}
          betterFor={[
            { color: 'blue', label: t('medium_dogs'), icon: 'dog' },
            { color: 'green', label: t('large_dogs'), icon: 'dog' },
            { color: 'green', label: t('extra_large_dogs'), icon: 'dog' },
          ]}
          showSaleBadge={true}
          freeTrialAvailable={!isRedeemFlow && !inApp && isFreeTrialVisible}
        />
      ),
    },
    {
      key: 'm',
      visible: true,
      node: (
        <ProductCard
          title="Mini"
          description={t('product_description_mini')}
          image={FiMiniImage}
          cta={isRedeemFlow ? t('claim_free_mini') : t('shop_fi_mini')}
          onClick={() => {
            productListingPage.miniShopNow();
            history.push(
              isRedeemFlow
                ? `${AppPaths.MiniRedeem}?discount=${encodeURIComponent(discountParam ?? '')}&type=dog`
                : AppPaths.Products('fi-mini') + '?type=dog',
            );
          }}
          hardware={[
            t('attachable_to_collars_and_harnesses'),
            t('small_and_lightweight'),
            miniBatteryLife,
            { text: t('fi_intelligence_enabled'), icon: <IconMagic /> },
          ]}
          betterFor={[
            { color: 'yellow', label: t('small_dogs'), icon: 'dog' },
            { color: 'yellow', label: t('puppies'), icon: 'dog' },
            { color: 'blue', label: t('medium_dogs'), icon: 'dog' },
          ]}
          showSaleBadge={true}
          freeTrialAvailable={!isRedeemFlow && !inApp && isFreeTrialVisible}
        />
      ),
    },
    {
      key: 'u',
      visible: isUltraSupportedInCountry && !isRedeemFlow,
      node: (
        <ProductCard
          title="Ultra"
          description={t('product_description_ultra')}
          image={FiUltraImage}
          cta={t('shop_fi_ultra')}
          onClick={() => {
            productListingPage.ultraShopNow();
            history.push(AppPaths.Products('fi-ultra'));
          }}
          hardware={[
            t('attachable_to_collars_and_harnesses'),
            t('satellite_tracking_with_starlink'),
            t('sound_and_vibration_feedback'),
          ]}
          betterFor={[
            { color: 'blue', label: t('medium_dogs'), icon: 'dog' },
            { color: 'green', label: t('large_dogs'), icon: 'dog' },
            { color: 'green', label: t('extra_large_dogs'), icon: 'dog' },
          ]}
          showSaleBadge={true}
          freeTrialAvailable={false}
        />
      ),
    },
    {
      // Cat free trial not currently supported - mirrors MiniPDP gating (see MiniPDP.tsx lines 240-247).
      // When cat free trial is enabled in MiniPDP, update freeTrialAvailable to use isFreeTrialVisible.
      key: 'mc',
      visible: !isRedeemFlow && !isReferralV2,
      node: (
        <ProductCard
          title="Mini"
          subtitle={t('for_cats')}
          description={t('product_description_mini_cats')}
          image={FiMiniForCatsImage}
          cta={t('shop_fi_mini')}
          onClick={() => {
            productListingPage.miniCatsShopNow();
            history.push(AppPaths.Products('fi-mini') + '?type=cat');
          }}
          hardware={[t('attachable_to_collars_and_harnesses'), t('small_and_lightweight'), miniBatteryLife]}
          betterFor={[{ color: 'yellow', label: t('cats'), icon: 'cat' }]}
          showSaleBadge={true}
          freeTrialAvailable={false}
        />
      ),
    },
  ];
  const orderedProductCards = orderProductCards(productCards, orderParam);

  return (
    <div className={classNames('product-listing-page', styles.container)}>
      <Flex direction={'column'} gap={16} align="center">
        <Text
          size={isReferralV2 ? 36 : 56}
          fw={700}
          lh={1}
          sx={{ letterSpacing: '-1.12px', textAlign: isReferralV2 ? 'center' : undefined }}
        >
          {isRedeemFlow ? t('exclusive_offer_claim_your_free_fi_collar') : t('trusted_by_millions_of_pet_parents')}
        </Text>
        <Text
          size={20}
          fw={700}
          lh={1.2}
          sx={(theme) => ({
            letterSpacing: '-0.4px',
            color: theme.colors['type-light-secondary'],
            textAlign: isReferralV2 ? 'center' : undefined,
          })}
        >
          {isRedeemFlow
            ? t('99_off_applied_at_checkout')
            : isReferralV2
              ? referrerName
                ? t('referral_v2_plp_subline', { referrerName })
                : t('referral_v2_plp_subline_generic')
              : t('most_advanced_gps_tracking_solutions_for_your_sidekick')}
        </Text>
        {isReferralV2 && <StarRating />}
        {!isRedeemFlow && !isReferralV2 && (
          <Button
            bg={'black'}
            radius={100}
            sx={{
              '&:hover, &:focus, &:active': { background: 'black' },
            }}
            onClick={() => {
              quizQuestionPage.quizButtonTapped();
              launchQuiz();
            }}
          >
            <Text size={14} fw={600} lh={1.2} py={10} px={24} sx={{ letterSpacing: '-0.14px' }}>
              {t('which_fi_is_right_for_me')}
            </Text>
          </Button>
        )}
      </Flex>
      <ProductCarousel>
        {orderedProductCards.map((card) => (
          <React.Fragment key={card.key}>{card.node}</React.Fragment>
        ))}
      </ProductCarousel>

      {!isReferralV2 && (
        <div className={styles.compareBox}>
          <div className={styles.compareTitle}>{t('not_sure_which_fi_is_for_you')}</div>
          <div className={styles.compareButtonsContainer}>
            <button
              className={styles.helpMeChooseCta}
              onClick={() => {
                quizQuestionPage.quizButtonTapped();
                launchQuiz();
              }}
            >
              {t('help_me_choose')}
            </button>
            <button
              className={styles.compareCta}
              onClick={() => {
                productListingPage.comparisonCompareProducts();
                history.push(AppPaths.Compare);
              }}
            >
              {t('compare_products')}
            </button>
          </div>
        </div>
      )}

      {isFreeTrialVisible && !isRedeemFlow && !inApp && !isReferralV2 && (
        <FreeTrialCard
          onStartFreeTrial={() => {
            productListingPage.freeTrialStartFreeTrial();
            launchQuiz();
          }}
        />
      )}

      {!isReferralV2 && (
        <>
          <div className={styles.keepShoppingTitle}>{t('keep_shopping')}</div>
          <ProductCarousel>
            <ProductCard
              title={t('accessories')}
              description={t('shop_bases_collars_attachments_and_more')}
              image={FiAccessoriesImage}
              cta={t('shop_all')}
              onClick={() => {
                productListingPage.keepShoppingAccessories();
                history.push(AppPaths.Accessories);
              }}
            />
            {supplementsProductIsSupportedInCountry(country) && (
              <ProductCard
                title={t('supplements')}
                description={t('data_driven_formulas_for_daily_dog_care')}
                image={FiSupplementsImage}
                cta={t('shop_all')}
                onClick={() => {
                  productListingPage.keepShoppingSupplements();
                  history.push(AppPaths.Products('supplements'));
                }}
              />
            )}
            {nanoProductIsSupportedInCountry(country) && (
              <ProductCard
                title={t('nano_microchips')}
                description={t('microchips_for_vets_and_rescue_shelters')}
                image={FiNanoMicrochipsImage}
                cta={t('shop_all')}
                onClick={() => {
                  productListingPage.keepShoppingNano();
                  window.location.href = `https://${getDomainConfig().nanoDomain}/`;
                }}
              />
            )}
          </ProductCarousel>
        </>
      )}
      <div className={styles.footer} />
    </div>
  );
};

export default ProductListingPage;
